import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stethoscope, Scales, ChartLineUp, Briefcase, ArrowLeft, ArrowRight, Check, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ICONS = { Stethoscope, Scales, ChartLineUp, Briefcase };
const STEPS = ["Sector", "Objetivo", "Datos"];

const empty = { sector: "", objectives: [], goal: "", name: "", email: "", phone: "", company: "" };

export default function DiagnosticWizard({ open, onOpenChange, content, presetSector }) {
  const sectors = content?.sectors?.items || [];
  const objectives = content?.cta?.objectives || [];
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0); setDone(false);
      setForm({ ...empty, sector: presetSector || "" });
    }
  }, [open, presetSector]);

  const selectedSector = useMemo(() => sectors.find((s) => s.key === form.sector), [sectors, form.sector]);

  const toggleObjective = (obj) => {
    setForm((f) => ({
      ...f,
      objectives: f.objectives.includes(obj) ? f.objectives.filter((o) => o !== obj) : [...f.objectives, obj],
    }));
  };

  const canNext = step === 0 ? !!form.sector : step === 1 ? form.objectives.length > 0 || form.goal.trim() : true;

  const next = () => { if (canNext) setStep((s) => Math.min(2, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Indica tu nombre y email."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/diagnostic", form);
      setDone(true);
      toast.success(data.message || "¡Solicitud enviada!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al enviar.");
    } finally { setSending(false); }
  };

  const inp = "w-full bg-transparent border-b border-border py-3 font-sans text-base text-ink placeholder:text-faint outline-none focus:border-ink transition-colors duration-300";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden bg-white" data-testid="diagnostic-wizard">
        <DialogTitle className="sr-only">Solicitar Diagnóstico de Innovación</DialogTitle>
        <DialogDescription className="sr-only">Formulario guiado de tres pasos: sector, objetivo y datos de contacto.</DialogDescription>
        {done ? (
          <div className="px-8 py-16 text-center" data-testid="diagnostic-success">
            <CheckCircle size={56} weight="thin" className="text-ink mx-auto" />
            <h3 className="font-serif text-3xl text-ink mt-6">¡Solicitud recibida!</h3>
            <p className="font-sans text-subtle mt-3 max-w-sm mx-auto">Gracias, {form.name.split(" ")[0]}. Revisaremos tu diagnóstico y te contactaremos muy pronto.</p>
            <button data-testid="diagnostic-close" onClick={() => onOpenChange(false)} className="mt-8 px-7 py-3 rounded-full bg-ink text-stone-50 font-sans text-sm hover:bg-black transition-colors duration-300">Cerrar</button>
          </div>
        ) : (
          <>
            <div className="px-8 pt-8 pb-6 border-b border-border">
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-faint">Diagnóstico de Innovación</p>
              <h3 className="font-serif text-2xl md:text-3xl text-ink mt-2 leading-tight">
                {step === 0 && "¿En qué sector trabajas?"}
                {step === 1 && "¿Qué quieres lograr?"}
                {step === 2 && "¿Cómo te contactamos?"}
              </h3>
              <div className="flex items-center gap-2 mt-5">
                {STEPS.map((label, i) => (
                  <div key={label} className="flex items-center gap-2 flex-1">
                    <div className={`h-1 rounded-full flex-1 transition-colors duration-400 ${i <= step ? "bg-ink" : "bg-stone-200"}`} />
                  </div>
                ))}
              </div>
              <p className="font-sans text-xs text-faint mt-2">Paso {step + 1} de 3 · {STEPS[step]}</p>
            </div>

            <div className="px-8 py-7 min-h-[280px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                >
                  {step === 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {sectors.map((s) => {
                        const Icon = ICONS[s.icon] || Briefcase;
                        const on = form.sector === s.key;
                        return (
                          <button
                            key={s.key}
                            data-testid={`wizard-sector-${s.key}`}
                            onClick={() => setForm((f) => ({ ...f, sector: s.key }))}
                            className={`flex flex-col items-start gap-3 p-4 rounded-xl border text-left transition-[background-color,border-color] duration-300 ${on ? "bg-ink border-ink" : "bg-white border-border hover:border-ink/40"}`}
                          >
                            <Icon size={24} weight="light" className={on ? "text-stone-50" : "text-ink"} />
                            <span className={`font-serif text-lg leading-tight ${on ? "text-stone-50" : "text-ink"}`}>{s.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <div className="flex flex-wrap gap-2.5">
                        {objectives.map((obj) => {
                          const on = form.objectives.includes(obj);
                          return (
                            <button
                              key={obj}
                              data-testid={`wizard-objective-${obj}`}
                              onClick={() => toggleObjective(obj)}
                              className={`flex items-center gap-2 px-4 py-2.5 rounded-full border font-sans text-sm transition-[background-color,border-color] duration-300 ${on ? "bg-ink text-stone-50 border-ink" : "bg-white text-subtle border-border hover:border-ink/40"}`}
                            >
                              {on && <Check size={14} weight="bold" />} {obj}
                            </button>
                          );
                        })}
                      </div>
                      <textarea
                        data-testid="wizard-goal"
                        rows={3}
                        value={form.goal}
                        onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
                        placeholder="Cuéntanos brevemente tu objetivo (opcional)"
                        className={inp + " mt-6 resize-none"}
                      />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5">
                      <div className="grid md:grid-cols-2 gap-5">
                        <input data-testid="wizard-name" className={inp} placeholder="Nombre*" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        <input data-testid="wizard-email" type="email" className={inp} placeholder="Email*" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                      </div>
                      <div className="grid md:grid-cols-2 gap-5">
                        <input data-testid="wizard-phone" className={inp} placeholder="Teléfono" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                        <input data-testid="wizard-company" className={inp} placeholder="Empresa / Consulta" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} />
                      </div>
                      <div className="rounded-xl bg-stone-50 border border-border p-4 font-sans text-sm text-subtle">
                        <span className="text-ink font-medium">{selectedSector?.label || "Sector"}</span>
                        {form.objectives.length > 0 && <> · {form.objectives.length} objetivo(s)</>}
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="px-8 py-5 border-t border-border flex items-center justify-between">
              <button
                data-testid="wizard-back"
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-2 font-sans text-sm text-subtle hover:text-ink transition-colors duration-300 disabled:opacity-0"
              >
                <ArrowLeft size={16} /> Atrás
              </button>
              {step < 2 ? (
                <button
                  data-testid="wizard-next"
                  onClick={next}
                  disabled={!canNext}
                  className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-stone-50 font-sans text-sm hover:bg-black transition-colors duration-300 disabled:opacity-40"
                >
                  Continuar <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              ) : (
                <button
                  data-testid="wizard-submit"
                  onClick={submit}
                  disabled={sending}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-stone-50 font-sans text-sm hover:bg-black transition-colors duration-300 disabled:opacity-50"
                >
                  {sending ? "Enviando…" : "Solicitar diagnóstico"}
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
